import { motion, AnimatePresence } from 'framer-motion';
import {
    Camera, Info, Sparkles, Shield, Activity, Star,
    FileDown, ClipboardList, HeadphonesIcon, LineChart, Users, Target, Zap, Clock, Download, MessageCircle, ArrowRight, Check, HelpCircle, BookOpen, Crown, XCircle, Search, FileText, Gift,
    Sun,
    Smile,
    PersonStanding,
    SplitSquareVertical,
    Images,
    Ruler,
    Focus,
    SunDim,
    ImageOff,
    Move,
    ScissorsSquare,
    AlertCircle,
    Stethoscope,
    HeartPulse,
    Scan,
    Apple,
    Fingerprint
} from 'lucide-react';
import { useState } from 'react';

const TipsGuide = () => {
    const [selectedCategory, setSelectedCategory] = useState('start');
    const [isHelpOpen, setIsHelpOpen] = useState(false);

    const categories = [
        {
            id: 'start',
            label: 'Primeros Pasos',
            icon: Star,
            color: 'bg-amber-500',
            gradient: 'from-amber-500 to-orange-500'
        },
        {
            id: 'photos',
            label: 'Mejores Fotos',
            icon: Camera,
            color: 'bg-blue-500',
            gradient: 'from-blue-500 to-blue-600'
        },
        {
            id: 'analysis',
            label: 'Análisis',
            icon: Activity,
            color: 'bg-emerald-500',
            gradient: 'from-emerald-500 to-emerald-600'
        },
        {
            id: 'results',
            label: 'Resultados',
            icon: Shield,
            color: 'bg-purple-500',
            gradient: 'from-purple-500 to-purple-600'
        }
    ];

    const tips = {
        start: [
            {
                title: 'Bienvenido a DogScan AI',
                subtitle: 'La plataforma líder en análisis de salud para mascotas',
                description: 'Analiza la salud de tu mascota con nuestra tecnología de inteligencia artificial de última generación y recibe asesoría de veterinarios certificados.',
                icon: BookOpen,
                steps: [
                    {
                        title: 'Análisis Instantáneo',
                        description: 'Sube hasta 5 fotos de tu mascota para un análisis completo',
                        icon: Camera
                    },
                    {
                        title: 'Elige tu Análisis',
                        description: 'Opción básica o análisis completo que incluye:',
                        subItems: [
                            'Evaluación de salud general',
                            'Recomendaciones nutricionales',
                            'Cuidados específicos de raza'
                        ],
                        icon: Sparkles
                    },
                    {
                        title: 'Reporte Detallado',
                        description: 'Recibe un informe completo sobre la salud de tu mascota',
                        icon: FileText
                    },
                    {
                        title: 'Consulta Profesional',
                        description: 'Acceso a veterinarios certificados 24/7',
                        icon: MessageCircle
                    },
                    {
                        title: 'Documentación',
                        description: 'Descarga el PDF del análisis y compártelo con tu veterinario',
                        icon: FileDown
                    },
                    {
                        title: 'Seguimiento',
                        description: 'Mantén un historial completo de la salud de tu mascota',
                        icon: LineChart
                    },
                    {
                        title: 'Comunidad',
                        description: 'Únete a una red de dueños responsables',
                        icon: Users
                    }
                ]
            },
            {
                title: '¿Por qué elegir DogScan AI?',
                description: 'Tecnología avanzada y atención profesional para el cuidado integral de tu mascota.',
                icon: HelpCircle,
                features: [
                    {
                        title: 'Precisión',
                        description: 'Análisis con 90% de precisión gracias a nuestra IA especializada',
                        stats: '90%',
                        color: 'emerald',
                        icon: Target
                    },
                    {
                        title: 'Rapidez',
                        description: 'Evaluación instantánea en menos de 1 minuto',
                        stats: '< 1min',
                        color: 'blue',
                        icon: Zap
                    },
                    {
                        title: 'Atención 24/7',
                        description: 'Acceso a veterinarios certificados en cualquier momento',
                        stats: '24/7',
                        color: 'purple',
                        icon: Clock
                    },
                    {
                        title: 'Seguimiento Completo',
                        description: 'Historial detallado de todos los reportes de salud',
                        stats: '100%',
                        color: 'amber',
                        icon: ClipboardList
                    }
                ],
                benefits: [
                    {
                        icon: Shield,
                        title: 'Prevención Temprana',
                        description: 'Detecta posibles problemas de salud antes de que sean graves'
                    },
                    {
                        icon: Clock,
                        title: 'Ahorro de Tiempo',
                        description: 'Evaluaciones instantáneas sin salir de casa'
                    },
                    {
                        icon: Star,
                        title: 'Especialización',
                        description: 'Análisis específicos por raza y edad'
                    },
                    {
                        icon: HeadphonesIcon,
                        title: 'Soporte Premium',
                        description: 'Atención personalizada y seguimiento continuo'
                    }
                ],
                callToAction: {
                    title: 'Comienza Ahora',
                    description: 'Obtén tu primer análisis gratuito',
                    button: 'Analizar Mascota',
                    secondaryButton: 'Ver Demo'
                }
            }
        ],
        photos: [
            {
                title: 'Guía para Fotos Perfectas',
                description: 'Maximiza la precisión del análisis siguiendo estas pautas:',
                icon: Camera,
                tips: [
                    'Luz natural o buena iluminación',
                    'Mascota en posición natural y relajada',
                    'Toma fotos de cuerpo completo',
                    'Incluye vistas laterales y frontales',
                    'Evita fondos saturados o confusos',
                    'Mantén una distancia de 2-3 metros'
                ]
            },
            {
                title: 'Qué Evitar',
                description: 'Para obtener mejores resultados, evita:',
                icon: XCircle,
                warnings: [
                    'Fotos borrosas o movidas',
                    'Iluminación insuficiente',
                    'Fondos muy complejos',
                    'Mascotas en movimiento',
                    'Fotos recortadas o editadas'
                ]
            }
        ],
        analysis: [
            {
                title: 'Proceso de Análisis',
                description: 'Nuestro sistema realiza una evaluación completa que incluye:',
                icon: Search,
                aspects: [
                    {
                        text: 'Evaluación Física General',
                        description: 'Análisis completo del estado físico actual, incluyendo medidas, peso y signos vitales',
                        color: 'bg-blue-100 text-blue-700',
                        icon: Stethoscope,
                        bgColor: 'bg-blue-50',
                        borderColor: 'border-blue-100'
                    },
                    {
                        text: 'Análisis Postural y de Movimiento',
                        description: 'Evaluación de la marcha, postura y patrones de movimiento para detectar anomalías',
                        color: 'bg-emerald-100 text-emerald-700',
                        icon: Activity,
                        bgColor: 'bg-emerald-50',
                        borderColor: 'border-emerald-100'
                    },
                    {
                        text: 'Salud Dermatológica',
                        description: 'Estado de piel, pelaje y signos de alergias o condiciones dermatológicas',
                        color: 'bg-amber-100 text-amber-700',
                        icon: Scan,
                        bgColor: 'bg-amber-50',
                        borderColor: 'border-amber-100'
                    },
                    {
                        text: 'Indicadores de Estrés',
                        description: 'Detección de signos de ansiedad y comportamientos relacionados con el estrés',
                        color: 'bg-purple-100 text-purple-700',
                        icon: HeartPulse,
                        bgColor: 'bg-purple-50',
                        borderColor: 'border-purple-100'
                    },
                    {
                        text: 'Nutrición y Peso',
                        description: 'Evaluación del estado nutricional y recomendaciones de dieta personalizadas',
                        color: 'bg-green-100 text-green-700',
                        icon: Apple,
                        bgColor: 'bg-green-50',
                        borderColor: 'border-green-100'
                    },
                    {
                        text: 'Perfil de Raza',
                        description: 'Análisis específico según características y predisposiciones de la raza',
                        color: 'bg-indigo-100 text-indigo-700',
                        icon: Fingerprint,
                        bgColor: 'bg-indigo-50',
                        borderColor: 'border-indigo-100'
                    },
                    {
                        text: 'Plan Preventivo',
                        description: 'Recomendaciones de cuidado preventivo y control parasitario',
                        color: 'bg-rose-100 text-rose-700',
                        icon: Shield,
                        bgColor: 'bg-rose-50',
                        borderColor: 'border-rose-100'
                    },
                    {
                        text: 'Alertas de Salud',
                        description: 'Identificación de aspectos que requieren atención o seguimiento veterinario',
                        color: 'bg-red-100 text-red-700',
                        icon: AlertCircle,
                        bgColor: 'bg-red-50',
                        borderColor: 'border-red-100'
                    }
                ]
            },
            {
                title: 'Información Importante',
                description: 'Para mejorar la precisión, proporciona detalles sobre:',
                icon: Info,
                checklist: [
                    'Cambios en el apetito',
                    'Nivel de actividad física',
                    'Patrones de sueño',
                    'Cambios de comportamiento',
                    'Síntomas específicos observados'
                ]
            }
        ],
        results: [
            {
                title: 'Interpretación de Resultados',
                description: 'Tu reporte detallado incluye:',
                icon: FileText,
                sections: [
                    { text: 'Estado general de salud', details: 'Evaluación integral del estado físico' },
                    { text: 'Áreas de atención', details: 'Aspectos que requieren seguimiento' },
                    { text: 'Recomendaciones', details: 'Sugerencias personalizadas de cuidado' },
                    { text: 'Plan de acción', details: 'Pasos a seguir para el bienestar de tu mascota' }
                ]
            },
            {
                title: 'Seguimiento Recomendado',
                description: 'Después del análisis:',
                icon: ArrowRight,
                steps: [
                    'Revisa el reporte completo',
                    'Agenda consulta veterinaria si es necesario',
                    'Descarga tu reporte en PDF si es necesario',
                    'Implementa las recomendaciones',
                    'Programa análisis de seguimiento',
                    'Mantén actualizado el historial'
                ]
            }
        ],
        plans: [
            {
                title: 'Planes Disponibles',
                description: 'Elige el plan que mejor se adapte a tus necesidades:',
                icon: Crown,
                plans: [
                    {
                        name: 'Básico',
                        features: [
                            '5 análisis mensuales',
                            'Reportes básicos de salud',
                            'Historial de análisis',
                            '10% descuento en consultas'
                        ],
                        color: 'bg-blue-100 text-blue-700'
                    },
                    {
                        name: 'Intermedio',
                        features: [
                            '15 análisis mensuales',
                            'Reportes detallados',
                            'Historial completo',
                            '20% descuento en consultas',
                            'Soporte prioritario'
                        ],
                        color: 'bg-amber-100 text-amber-700'
                    },
                    {
                        name: 'Premium',
                        features: [
                            'Análisis ilimitados',
                            'Reportes avanzados',
                            'Historial detallado',
                            '30% descuento en consultas',
                            'Soporte 24/7',
                            'Acceso anticipado a funciones'
                        ],
                        color: 'bg-purple-100 text-purple-700'
                    }
                ]
            },
            {
                title: 'Beneficios Adicionales',
                description: 'Todos los planes incluyen:',
                icon: Gift,
                benefits: [
                    'Acceso a la plataforma',
                    'Análisis con IA de última generación',
                    'Reportes descargables en PDF',
                    'Acceso a veterinarios certificados',
                    'Guías de cuidado personalizado'
                ]
            }
        ]
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-8">
            <div className="max-w-4xl mx-auto px-4 sm:px-6">
                {/* Header */}
                <motion.div className="text-center mb-10">
                    <div className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500/10 to-orange-500/10 
                        px-4 py-2 rounded-full mb-4">
                        <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                        >
                            <HelpCircle className="h-5 w-5 text-amber-600" />
                        </motion.div>
                        <span className="font-medium text-amber-700">
                            ¿Necesitas ayuda?
                        </span>
                    </div>

                    <h2 className="text-2xl sm:text-3xl font-bold mb-4 text-gray-900">
                        Guía de DogScan AI
                    </h2>
                </motion.div>

                {/* Category Navigation */}
                <div className="flex flex-wrap gap-2 justify-center mb-8 px-4">
                    {categories.map((category) => (
                        <motion.button
                            key={category.id}
                            onClick={() => setSelectedCategory(category.id)}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium 
                                shadow-sm transition-all duration-200 
                                ${selectedCategory === category.id
                                    ? `bg-gradient-to-r ${category.gradient} text-white shadow-md`
                                    : 'bg-white text-gray-700 hover:bg-gray-50'
                                }`}
                        >
                            <category.icon className="h-4 w-4" />
                            {category.label}
                        </motion.button>
                    ))}
                </div>

                {/* Content Sections */}
                <AnimatePresence mode="wait">
                    <motion.div
                        key={selectedCategory}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.3 }}
                        className="space-y-6"
                    >
                        {tips[selectedCategory].map((section, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 }}
                                className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 
                                    hover:shadow-md transition-all duration-200"
                            >
                                {/* Section Header */}
                                <div className="flex items-start gap-4 mb-6">
                                    <div className="p-3 rounded-xl bg-gradient-to-r from-amber-50 to-orange-50">
                                        <section.icon className="h-6 w-6 text-amber-500" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900">
                                            {section.title}
                                        </h3>
                                        {section.subtitle && (
                                            <p className="text-sm text-amber-600 mt-1">
                                                {section.subtitle}
                                            </p>
                                        )}
                                        <p className="text-gray-600 text-sm mt-2">
                                            {section.description}
                                        </p>
                                    </div>
                                </div>
                                {section.tips && (
                                    <motion.div
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        className="mt-6 grid gap-4 sm:grid-cols-2"
                                    >
                                        {section.tips.map((tip, i) => {
                                            // Custom icon for each tip
                                            const TipIcon = (() => {
                                                switch (i) {
                                                    case 0: return Sun; // For "Luz natural"
                                                    case 1: return Smile; // For "posición natural y relajada"
                                                    case 2: return PersonStanding; // For "cuerpo completo"
                                                    case 3: return SplitSquareVertical; // For "vistas laterales y frontales"
                                                    case 4: return Images; // For "fondos"
                                                    case 5: return Ruler; // For "distancia"
                                                    default: return Camera;
                                                }
                                            })();

                                            return (
                                                <motion.div
                                                    key={i}
                                                    initial={{ opacity: 0, y: 10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ delay: i * 0.1 }}
                                                    className="flex items-start gap-3 bg-blue-50/50 rounded-lg p-4"
                                                >
                                                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 
                        flex items-center justify-center mt-0.5">
                                                        <TipIcon className="h-3.5 w-3.5 text-blue-600" />
                                                    </div>
                                                    <span className="text-gray-700 text-sm">
                                                        {tip}
                                                    </span>
                                                </motion.div>
                                            );
                                        })}
                                    </motion.div>
                                )}
                                {section.warnings && (
                                    <motion.div
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        className="mt-6 grid gap-4"
                                    >
                                        {section.warnings.map((warning, i) => {
                                            // Custom icon for each warning
                                            const WarningIcon = (() => {
                                                switch (i) {
                                                    case 0: return Focus; // For "fotos borrosas"
                                                    case 1: return SunDim; // For "iluminación insuficiente"
                                                    case 2: return ImageOff; // For "fondos complejos"
                                                    case 3: return Move; // For "mascotas en movimiento"
                                                    case 4: return ScissorsSquare; // For "fotos recortadas"
                                                    default: return AlertCircle;
                                                }
                                            })();

                                            return (
                                                <motion.div
                                                    key={i}
                                                    initial={{ opacity: 0, y: 10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ delay: i * 0.1 }}
                                                    className="flex items-start gap-3 bg-red-50 rounded-lg p-4"
                                                >
                                                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-red-100 
                        flex items-center justify-center mt-0.5">
                                                        <WarningIcon className="h-3.5 w-3.5 text-red-600" />
                                                    </div>
                                                    <span className="text-gray-700 text-sm">
                                                        {warning}
                                                    </span>
                                                </motion.div>
                                            );
                                        })}
                                    </motion.div>
                                )}
                                {/* Replace the direct aspects mapping with this conditional rendering */}
                                {section.aspects && (
                                    <motion.div
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        className="mt-6 grid gap-4 sm:grid-cols-2"
                                    >
                                        {section.aspects.map((aspect, i) => (
                                            <motion.div
                                                key={i}
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: i * 0.1 }}
                                                className={`flex flex-col gap-2 ${aspect.bgColor} rounded-lg p-4 
                    border ${aspect.borderColor} hover:shadow-sm transition-all duration-200`}
                                            >
                                                <div className="flex items-start gap-3">
                                                    <div className={`flex-shrink-0 w-10 h-10 rounded-lg ${aspect.color} 
                        flex items-center justify-center`}>
                                                        <aspect.icon className="h-5 w-5" />
                                                    </div>
                                                    <div className="flex-1">
                                                        <h4 className={`font-medium text-gray-900 mb-1`}>
                                                            {aspect.text}
                                                        </h4>
                                                        <p className="text-sm text-gray-600">
                                                            {aspect.description}
                                                        </p>
                                                    </div>
                                                </div>
                                            </motion.div>
                                        ))}
                                    </motion.div>
                                )}
                                {section.checklist && (
                                    <motion.div
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        className="mt-6 space-y-3"
                                    >
                                        {section.checklist.map((item, i) => (
                                            <motion.div
                                                key={i}
                                                initial={{ opacity: 0, x: -20 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{ delay: i * 0.1 }}
                                                className="flex items-center gap-3 bg-gray-50 rounded-lg p-3"
                                            >
                                                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-100 
                    flex items-center justify-center">
                                                    <Check className="h-4 w-4 text-green-600" />
                                                </div>
                                                <span className="text-gray-700 text-sm">
                                                    {item}
                                                </span>
                                            </motion.div>
                                        ))}
                                    </motion.div>
                                )}

                                {section.steps && Array.isArray(section.steps) && (
                                    <div className="space-y-6">
                                        {/* Check if steps are complex objects or simple strings */}
                                        {typeof section.steps[0] === 'object' ? (
                                            // Complex steps with title and description
                                            section.steps.map((step, i) => (
                                                <motion.div
                                                    key={i}
                                                    initial={{ opacity: 0, x: -20 }}
                                                    animate={{ opacity: 1, x: 0 }}
                                                    transition={{ delay: i * 0.1 }}
                                                    className="flex items-start gap-4"
                                                >
                                                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100/50 
                        flex items-center justify-center">
                                                        <step.icon className="h-5 w-5 text-amber-600" />
                                                    </div>
                                                    <div className="flex-1">
                                                        <h4 className="font-medium text-gray-900">
                                                            {step.title}
                                                        </h4>
                                                        <p className="text-gray-600 text-sm mt-1">
                                                            {step.description}
                                                        </p>
                                                        {step.subItems && (
                                                            <ul className="mt-2 space-y-1">
                                                                {step.subItems.map((item, j) => (
                                                                    <li key={j} className="flex items-center gap-2 text-sm text-gray-600">
                                                                        <Check className="h-3 w-3 text-amber-500" />
                                                                        {item}
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        )}
                                                    </div>
                                                </motion.div>
                                            ))
                                        ) : (
                                            // Simple string steps
                                            <ul className="space-y-3">
                                                {section.steps.map((step, i) => (
                                                    <motion.li
                                                        key={i}
                                                        initial={{ opacity: 0, x: -20 }}
                                                        animate={{ opacity: 1, x: 0 }}
                                                        transition={{ delay: i * 0.1 }}
                                                        className="flex items-start gap-3"
                                                    >
                                                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-amber-100 
                            flex items-center justify-center">
                                                            <Check className="h-4 w-4 text-amber-600" />
                                                        </div>
                                                        <span className="text-gray-700">{step}</span>
                                                    </motion.li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                )}

                                {/* Add this inside your section mapping, alongside other conditional renders */}
                                {section.sections && (
                                    <motion.div
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        className="mt-6 grid gap-4"
                                    >
                                        {section.sections.map((item, i) => (
                                            <motion.div
                                                key={i}
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: i * 0.1 }}
                                                className="flex flex-col gap-2 bg-white rounded-lg p-4 border border-gray-100 
                    hover:shadow-sm transition-all duration-200"
                                            >
                                                <div className="flex items-start gap-3">
                                                    <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-purple-100 
                        flex items-center justify-center">
                                                        <FileText className="h-4 w-4 text-purple-600" />
                                                    </div>
                                                    <div className="flex-1">
                                                        <h4 className="font-medium text-gray-900 mb-1">
                                                            {item.text}
                                                        </h4>
                                                        <p className="text-sm text-gray-600">
                                                            {item.details}
                                                        </p>
                                                    </div>
                                                </div>
                                            </motion.div>
                                        ))}
                                    </motion.div>
                                )}

                                {/* Features Section */}
                                {section.features && (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
                                        {section.features.map((feature, i) => (
                                            <motion.div
                                                key={i}
                                                initial={{ opacity: 0, scale: 0.95 }}
                                                animate={{ opacity: 1, scale: 1 }}
                                                transition={{ delay: i * 0.1 }}
                                                className="p-4 rounded-xl bg-gradient-to-r from-white to-gray-50
                                                    border border-gray-100"
                                            >
                                                <div className="flex items-center gap-3 mb-2">
                                                    <feature.icon className="h-5 w-5 text-amber-500" />
                                                    <h4 className="font-medium text-gray-900">
                                                        {feature.title}
                                                    </h4>
                                                </div>
                                                <p className="text-sm text-gray-600">
                                                    {feature.description}
                                                </p>
                                                {feature.stats && (
                                                    <div className="mt-2 text-lg font-bold text-amber-600">
                                                        {feature.stats}
                                                    </div>
                                                )}
                                            </motion.div>
                                        ))}
                                    </div>
                                )}
                                {/* Add this to your section mapping, alongside other conditional renders */}
                                {section.plans && (
                                    <motion.div
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        className="mt-6 grid gap-6 sm:grid-cols-3"
                                    >
                                        {section.plans.map((plan, i) => {
                                            const PlanIcon = i === 2 ? Crown : i === 1 ? Star : Shield;

                                            return (
                                                <motion.div
                                                    key={i}
                                                    initial={{ opacity: 0, y: 10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ delay: i * 0.1 }}
                                                    className={`${plan.color} rounded-xl p-6 border border-gray-100 
                        hover:shadow-lg transition-all duration-200`}
                                                >
                                                    <div className="flex flex-col h-full">
                                                        <div className="mb-4 flex items-center gap-2">
                                                            <PlanIcon className="h-5 w-5 text-amber-500" />
                                                            <h4 className="text-lg font-semibold">
                                                                {plan.name}
                                                            </h4>
                                                        </div>

                                                        <ul className="space-y-3 flex-grow">
                                                            {plan.features.map((feature, j) => (
                                                                <li key={j} className="flex items-start gap-2">
                                                                    <Check className="h-4 w-4 text-green-500 mt-1 flex-shrink-0" />
                                                                    <span className="text-sm text-gray-700">
                                                                        {feature}
                                                                    </span>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                </motion.div>
                                            );
                                        })}
                                    </motion.div>
                                )}
                                {/* Benefits Section */}
                                {/* Benefits Section - Replace the current benefits section with this */}
                                {section.benefits && (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
                                        {section.benefits.map((benefit, i) => {
                                            // Check if benefit is an object or string
                                            if (typeof benefit === 'object') {
                                                const BenefitIcon = benefit.icon;
                                                return (
                                                    <motion.div
                                                        key={i}
                                                        initial={{ opacity: 0, y: 10 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        transition={{ delay: i * 0.1 }}
                                                        className="flex items-start gap-3 p-4 rounded-lg bg-gray-50"
                                                    >
                                                        <BenefitIcon className="h-5 w-5 text-amber-500 mt-0.5" />
                                                        <div>
                                                            <h4 className="font-medium text-gray-900">
                                                                {benefit.title}
                                                            </h4>
                                                            <p className="text-sm text-gray-600 mt-1">
                                                                {benefit.description}
                                                            </p>
                                                        </div>
                                                    </motion.div>
                                                );
                                            } else {
                                                // Handle string benefits
                                                return (
                                                    <motion.div
                                                        key={i}
                                                        initial={{ opacity: 0, x: -20 }}
                                                        animate={{ opacity: 1, x: 0 }}
                                                        transition={{ delay: i * 0.1 }}
                                                        className="flex items-start gap-2 p-3 rounded-lg bg-gray-50"
                                                    >
                                                        <Check className="h-4 w-4 text-amber-500 mt-1 flex-shrink-0" />
                                                        <span className="text-sm text-gray-700">{benefit}</span>
                                                    </motion.div>
                                                );
                                            }
                                        })}
                                    </div>
                                )}
                            </motion.div>
                        ))}
                    </motion.div>
                </AnimatePresence>

                {/* Footer */}
                <motion.div
                    className="mt-8 text-center"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                >
                    <button
                        onClick={() => window.open('https://wa.me/525643251394', '_blank')}
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-full 
                            bg-gradient-to-r from-amber-500 to-orange-500 text-white
                            hover:shadow-lg hover:scale-105 transition-all duration-200"
                    >
                        <MessageCircle className="h-5 w-5" />
                        ¿Necesitas ayuda? Contáctanos
                        <ArrowRight className="h-4 w-4 ml-1" />
                    </button>
                </motion.div>
            </div>
        </div>
    );
};

export default TipsGuide;